import React, { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { payment, formatCurrency } from '@futurefamily/ef-utils';
import {
  Button as DButton,
  PaymentOptionContainer,
  PaymentOptionItem,
  useTreatmentCost
} from '@futurefamily/ef-components';
import * as config from 'config';

const FULL_FINANCING_MINIMUM = 5000;

const cardStyles = {
  color: 'var(--white)',
  'border-radius': '40px',
  '--separator-color': 'var(--white)'
};

const Button = styled(DButton)`
  background-color: var(--white);
  color: var(--brown);
  &:hover {
    color: var(--brown);
  }
`;

const useOptions = () => {
  const { values } = useFormikContext();
  const { downpayment, term } = values;
  const treatmentCost = useTreatmentCost();
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const prequalUrl = `${config.APP_FLOW_URL}?${config.UTM_PARAMETERS}&vendorId=${config.VENDOR_ID}&vendorLocationId=${query.get('vendor')}`;

  return useMemo(() => {
    const financingOptions = [
      {
        name: 'Out of Pocket',
        paymentInfo: 'Upfront payment',
        cost: (
          <strong>${formatCurrency(treatmentCost)}</strong>
        ),
        apr: 'At time of service',
        style: {
          ...cardStyles,
          '--background-color': 'var(--teal)',
          '--border-color': "var(--teal)",
        },
        Button: () => <Button className="px-2" id="schedule-appointment" as="a" href={config.RETURN_URL}>SCHEDULE APPOINTMENT</Button>
      }
    ];

    if (treatmentCost >= FULL_FINANCING_MINIMUM) {
      const cost = treatmentCost;
      const fullFinance = payment.getMonthlyPayment(cost, term, config.APR).toFixed(0);

      financingOptions.push({
        name: 'Full Finance',
        paymentInfo: 'Pay with 100% financing',
        cost: (
          <>
            <strong>${formatCurrency(fullFinance)}</strong>/month*
          </>
        ),
        apr: `For ${term} months at ${String(config.APR)}%`,
        style: {
          ...cardStyles,
          '--background-color': 'var(--brown)',
          '--border-color': "var(--brown)",
        },
        showDisclosure: true,
        Button: () => <Button id="full-finance" as="a" href={`${prequalUrl}&amount=${cost}&term=${term}`}>PREQUALIFY NOW</Button>
      });

      const loanAmount = treatmentCost - downpayment;
      if (loanAmount >= config.LOAN_MINIMUM) {
        const cost = treatmentCost - downpayment;
        const partialFinance = payment.getMonthlyPayment(cost, term, config.APR).toFixed(0);

        financingOptions.push({
          name: 'Partial Finance',
          paymentInfo: 'Some payment and financing',
          cost: (
            <>
              <div>
                <strong>${formatCurrency(downpayment)}</strong> cash
              </div>
              <div>
                +<strong>${formatCurrency(partialFinance)}</strong>/month*
              </div>
            </>
          ),
          apr: `For ${term} months at ${String(config.APR)}%`,
          showDisclosure: true,
          style: {
            ...cardStyles,
            '--background-color': 'var(--blue)',
            '--border-color': "var(--blue)",
          },
          Button: () => <Button id="partial-finance" as="a" href={`${prequalUrl}&amount=${cost}&term=${term}`}>PREQUALIFY NOW</Button>
        });
      }
    }

    return financingOptions;
  }, [downpayment, treatmentCost, term, prequalUrl]);
};

function PaymentOptions () {
  const options = useOptions();

  return (
    <PaymentOptionContainer className="mb-4">
      {options.map((option, i) => (
        <PaymentOptionItem
          key={i}
          number={i + 1}
          {...option}
        />
      ))}
    </PaymentOptionContainer>
  );
}

export default PaymentOptions;
